/**
 * Export an array of flat objects to a downloadable CSV file.
 * Handles quoting/escaping and prepends a BOM so Excel opens UTF-8 cleanly.
 */
export function exportToCsv(filename: string, rows: readonly object[]): void {
  if (!rows || rows.length === 0) return;

  const records = rows as ReadonlyArray<Record<string, unknown>>;
  const headers = Object.keys(records[0]);

  const escape = (val: unknown): string => {
    if (val === null || val === undefined) return "";
    let s = String(val);
    // Neutralize spreadsheet formula injection: Excel/Sheets execute cells
    // beginning with = + - @ (or tab/CR). Prefix a quote to defuse them.
    // Numbers can't carry formulas, and prefixing would turn negatives into
    // text ('-12.5), so they are exempt.
    if (typeof val !== "number" && /^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const csv = [
    headers.join(","),
    ...records.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ].join("\r\n");

  // Prepend UTF-8 BOM (\uFEFF) so Excel opens the file with correct encoding
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(filename.endsWith(".csv") ? filename : `${filename}.csv`, blob);
}

/** Trigger a browser download for an in-memory Blob. */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Build a date-stamped filename, e.g. winback-targets_2026-06-09.csv */
export function stampedFilename(base: string): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
  return `${base}_${stamp}.csv`;
}
